import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Building2, Lock, Mail, Phone, User, MapPin, FileText, ScrollText, ShieldCheck, RefreshCw, Calculator } from 'lucide-react';
import { getMockUsers, saveMockUsers } from '../mock';
import { useToast } from '../hooks/use-toast';
import { validateEmail, validatePhone, formatPhoneNumber, getPhoneDigits } from '../utils/validation';
import { otpAPI } from '../services/api';
import MVMLogo from '../components/MVMLogo';

// Simple Math CAPTCHA Component
const MathCaptcha = ({ onVerify, verified, onRefresh }) => {
  const [num1, setNum1] = React.useState(0);
  const [num2, setNum2] = React.useState(0);
  const [operator, setOperator] = React.useState('+');
  const [userAnswer, setUserAnswer] = React.useState('');
  const [error, setError] = React.useState('');

  const generateNewProblem = React.useCallback(() => {
    const operators = ['+', '-', '×'];
    const op = operators[Math.floor(Math.random() * operators.length)];
    let n1, n2;
    
    if (op === '+') {
      n1 = Math.floor(Math.random() * 20) + 1;
      n2 = Math.floor(Math.random() * 20) + 1;
    } else if (op === '-') {
      n1 = Math.floor(Math.random() * 30) + 10;
      n2 = Math.floor(Math.random() * n1);
    } else {
      n1 = Math.floor(Math.random() * 10) + 1;
      n2 = Math.floor(Math.random() * 10) + 1;
    }
    
    setNum1(n1);
    setNum2(n2);
    setOperator(op);
    setUserAnswer('');
    setError('');
    onVerify(false);
  }, [onVerify]);

  React.useEffect(() => {
    generateNewProblem();
  }, []);

  const getCorrectAnswer = () => {
    switch (operator) {
      case '+': return num1 + num2;
      case '-': return num1 - num2;
      case '×': return num1 * num2;
      default: return num1 + num2;
    }
  };

  const handleVerify = () => {
    const correct = getCorrectAnswer();
    if (parseInt(userAnswer) === correct) {
      onVerify(true);
      setError('');
    } else {
      setError('Incorrect answer. Try again!');
      generateNewProblem();
    }
  };

  const handleRefresh = () => {
    generateNewProblem();
    if (onRefresh) onRefresh();
  };

  if (verified) {
    return (
      <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-lg">
        <ShieldCheck className="w-5 h-5 text-green-600" />
        <span className="text-sm text-green-700 font-medium">Verification passed</span>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 bg-gray-50 dark:bg-secondary border border-gray-200 dark:border-border rounded-lg space-y-3">
      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-muted-foreground">
        <Calculator className="w-4 h-4" />
        <span className="font-medium">Security Check - Solve this:</span>
      </div>
      
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-card border rounded-lg text-lg font-bold text-gray-800 dark:text-foreground">
          <span>{num1}</span>
          <span className="text-teal-600">{operator}</span>
          <span>{num2}</span>
          <span>=</span>
          <span className="text-gray-400 dark:text-muted-foreground">?</span>
        </div>
        
        <Input
          type="number"
          value={userAnswer}
          onChange={(e) => {
            setUserAnswer(e.target.value);
            setError('');
          }}
          placeholder="?"
          className="w-20 h-10 text-center text-lg font-bold"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleVerify();
            }
          }}
        />
        
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleVerify}
          disabled={!userAnswer}
          className="h-10"
        >
          Verify
        </Button>
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleRefresh}
          className="h-10 px-2"
          title="Get new problem"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
      
      {error && (
        <p className="text-xs text-red-600">{error}</p>
      )}
    </div>
  );
};

// Terms and Conditions Content - Easy to update
const TERMS_AND_CONDITIONS = {
  lastUpdated: "December 2024",
  content: `
## TERMS OF SERVICE AND SERVICE LEVEL AGREEMENT

**OPERATED BY: MYONK, BENGALURU**

---

### 1. THE LEGAL BINDING AGREEMENT

This document is a legally binding agreement between the user and MYONK. By accessing the PWA MyVenueMate.in, you acknowledge that you have read, understood, and agreed to be bound by these terms. This agreement governs all aspects of your use of the software, including lead management, booking modules, and data storage. If you do not agree to these terms, you are prohibited from using the application and must delete your account immediately.

### 2. ZERO LIABILITY AND ABSOLUTE DISCLAIMER

MYONK provides MyVenueMate.in on an "as-is" and "as-available" basis. The company makes no representations or warranties of any kind, express or implied, as to the operation of the service or the information, content, or materials included therein. To the full extent permissible by applicable law, MYONK disclaims all warranties, express or implied, including, but not limited to, implied warranties of merchantability and fitness for a particular purpose. The company does not warrant that the service, its servers, or emails sent from the company are free of viruses or other harmful components.

### 3. EXCLUSION OF CONSEQUENTIAL AND FINANCIAL LOSSES

Under no circumstances shall MYONK be liable for any damages of any kind arising from the use of this service, including, but not limited to direct, indirect, incidental, punitive, and consequential damages. This includes, but is not limited to, loss of business revenue, loss of event bookings, loss of customer goodwill, business interruption, or any other pecuniary loss. The user acknowledges that event management involves high stakes and that the software is merely a recording tool, not a guarantee of business success or operational accuracy.

### 4. DATA LOSS AND RECOVERY INDEMNITY

Data integrity is not guaranteed. MYONK shall not be held responsible for the loss of any data, including lead lists, customer contact details, venue availability calendars, or financial records. The user is strictly required to maintain their own independent manual or digital backups of all data entered into MyVenueMate.in. In the event of a server failure, database corruption, or accidental deletion, the company is under no legal obligation to recover lost data and shall not be liable for any business disruption caused by such loss.

### 5. SECRECY, PRIVACY, AND CYBER-SECURITY

While the company implements security protocols, the user acknowledges that the internet is an inherently insecure medium. MYONK does not guarantee that the use of the PWA will be private or secure. We are not liable for any unauthorized access to your account, data breaches, or the "leaking" of lead information to third parties due to hacking, phishing, or other malicious activities. The user assumes all risk regarding the confidentiality of their business information.

### 6. NO RESPONSIBILITY FOR THIRD-PARTY INFRASTRUCTURE

The MyVenueMate.in PWA relies on third-party infrastructure, including hosting providers, cloud storage, and internet service providers. MYONK has no control over these third parties. Any failure, downtime, or data breach occurring at the level of the hosting provider or the user's internet service provider is outside the scope of the company's responsibility. The company shall not be liable for any losses resulting from the failure of these third-party systems.

### 7. USER INDEMNIFICATION OBLIGATIONS

The user agrees to indemnify, defend, and hold harmless MYONK, its partners, officers, and employees from and against any and all claims, damages, obligations, losses, liabilities, costs, or debt, and expenses (including but not limited to attorney's fees) arising from: (i) your use of and access to the service; (ii) your violation of any term of these Terms; (iii) your violation of any third-party right, including without limitation any copyright, property, or privacy right; or (iv) any claim that your content caused damage to a third party. This defense and indemnification obligation will survive these Terms and your use of the service.

### 8. ERRORS, INACCURACIES, AND OMISSIONS

Occasionally there may be information on the PWA that contains typographical errors, inaccuracies, or omissions that may relate to booking dates, lead statuses, or venue availability. The company reserves the right to correct any errors, inaccuracies, or omissions, and to change or update information if any information in the service is inaccurate at any time without prior notice. The company is not responsible if information made available on this service is not accurate, complete, or current.

### 9. INTELLECTUAL PROPERTY RIGHTS

All content included on the PWA, such as text, graphics, logos, button icons, images, audio clips, digital downloads, data compilations, and software, is the property of MYONK and protected by international copyright laws. The user may not systematically extract or re-utilize parts of the contents of the service without express written consent. The "MyVenueMate" name and logo are trademarks of MYONK and may not be used in connection with any product or service that is not the company's.

### 10. TERMINATION OF SERVICE

MYONK reserves the right to terminate your access to the service or any part thereof at any time, without notice, for any reason whatsoever. Upon termination, your right to use the service will immediately cease. If you wish to terminate your account, you may simply discontinue using the service. All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.

### 11. FORCE MAJEURE

The company shall not be liable for any delay or failure in performance resulting directly or indirectly from acts of nature, forces, or causes beyond its reasonable control, including, without limitation, internet failures, computer equipment failures, telecommunication equipment failures, other equipment failures, electrical power failures, strikes, labor disputes, riots, insurrections, civil disturbances, shortages of labor or materials, fires, floods, storms, explosions, acts of God, war, governmental actions, orders of domestic or foreign courts or tribunals, or non-performance of third parties.

### 12. JURISDICTION AND DISPUTE RESOLUTION

These terms and conditions are governed by and construed in accordance with the laws of India. Any dispute arising out of or in connection with these terms, including any question regarding its existence, validity, or termination, shall be referred to and finally resolved by the courts of Bengaluru, Karnataka. The user agrees that regardless of any statute or law to the contrary, any claim or cause of action arising out of or related to use of the service or these terms must be filed within one year after such claim or cause of action arose or be forever barred.

---

## PRIVACY AND DATA HANDLING POLICY

**MYONK, BENGALURU**

### 1. DATA COLLECTION AND USAGE

MYONK collects information provided by the user to facilitate venue bookings and lead management. This includes names, phone numbers, and event details. This data is stored on cloud servers. The company uses this data solely to provide the service but does not guarantee its absolute privacy.

### 2. DATA PROCESSING AND THIRD PARTIES

The user acknowledges that MYONK acts as a data processor. The venue owner (user) is the data controller. It is the user's responsibility to ensure they have the consent of their customers to enter their data into MyVenueMate.in. The company may use third-party tools for analytics or hosting, and the user consents to their data being processed by these third parties.

### 3. NO LIABILITY FOR DATA BREACH

In the event of a security breach where personal data is accessed by unauthorized parties, MYONK's liability is limited to notifying the user of the breach. The company is not liable for any financial or reputational damage caused to the user or their customers as a result of such a breach.

### 4. COOKIES AND TRACKING

The PWA uses cookies and local storage to enhance user experience and maintain login sessions. By using the platform, the user consents to the use of these tracking technologies.

### 5. AMENDMENTS

MYONK reserves the right to amend these privacy and terms at any time. The updated version will be effective as soon as it is accessible on the PWA.

---

**By checking the box below, you confirm that you have read and agree to these Terms of Service.**
  `
};

// Privacy Policy Content - Separate from Terms
const PRIVACY_POLICY = {
  lastUpdated: "December 2024",
  content: `
## PRIVACY POLICY

**MYVENUEMATE.IN – OPERATED BY MYONK**

---

### 1. INTRODUCTION AND SCOPE

This Privacy Policy outlines how MYONK, based in Bengaluru, collects, processes, and protects information through the MyVenueMate.in Progressive Web Application (PWA). By using this platform, you agree to the collection and use of information in accordance with this policy. MYONK acts primarily as a service provider; we do not own the data entered by you regarding your leads, bookings, or customers.

### 2. ROLE AS DATA PROCESSOR

The user (Venue Owner/Manager) acknowledges that they are the "Data Controller" for all customer and lead information uploaded to the platform. MYONK acts as the "Data Processor." It is the sole responsibility of the user to ensure they have obtained necessary consent from their clients before entering personal information into MyVenueMate.in. MYONK shall not be held liable for any privacy violations committed by the user against their own clients.

### 3. INFORMATION COLLECTION

We collect several types of information for various purposes to provide and improve our service:

**User Account Data:** Name, business email, phone number, and venue address provided during registration.

**Business Data:** Lead details, booking schedules, pricing, and event requirements entered into the system.

**Usage Data:** IP addresses, browser type, device information, and time spent on the PWA to ensure technical stability.

### 4. USE OF DATA

MYONK uses the collected data for the following purposes:

- To provide and maintain the functionality of the booking management system.
- To notify you about changes to our service or subscription status.
- To provide customer support and troubleshoot technical errors.
- To monitor the usage of the service to prevent fraudulent activities.

### 5. DATA STORAGE AND SECRECY LIMITATIONS

All data is stored on secure third-party cloud servers. However, MYONK offers no guarantee of absolute secrecy. The user acknowledges that:

- No method of transmission over the internet or method of electronic storage is 100% secure.
- The Company is not responsible for data leaks resulting from "brute force" attacks, phishing, or vulnerabilities in third-party server infrastructures.
- In the event of a data breach, the Company's only obligation is to inform the user within a reasonable timeframe.

### 6. NO SALE OF DATA

MYONK does not sell, rent, or trade the business data or lead information entered by the user to any third-party marketing firms. The data is used strictly for the operational requirements of the MyVenueMate.in platform.

### 7. DISCLOSURE OF DATA

MYONK may disclose your personal or business data in the good faith belief that such action is necessary to:

- Comply with a legal obligation or government request in India.
- Protect and defend the rights or property of MYONK.
- Prevent or investigate possible wrongdoing in connection with the Service.
- Protect the personal safety of users of the Service or the public.

### 8. COOKIES AND TRACKING TECHNOLOGIES

The PWA utilizes cookies and similar tracking technologies (like local storage) to track the activity on our Service and hold certain information. Cookies are files with small amounts of data which may include an anonymous unique identifier. You can instruct your browser to refuse all cookies, but doing so may prevent you from using certain features of MyVenueMate.in.

### 9. DATA RETENTION AND DELETION

The Company will retain your data only for as long as is necessary for the purposes set out in this Privacy Policy. Upon termination of a subscription or account, MYONK reserves the right to delete all data associated with that account after a 30-day grace period. It is the user's responsibility to export their data prior to account termination. The Company is not liable for data that is unrecoverable after deletion.

### 10. THIRD-PARTY SERVICES

Our PWA may contain links to or integrations with third-party services (such as payment gateways or SMS providers). We have no control over and assume no responsibility for the privacy policies or practices of any third-party services.

### 11. CHANGES TO THIS PRIVACY POLICY

MYONK may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted.

### 12. JURISDICTION

This Privacy Policy is governed by the laws of India. Any disputes related to data privacy or data handling shall be subject to the exclusive jurisdiction of the courts in Bengaluru, Karnataka.

---

**By checking the box, you confirm that you have read and understood this Privacy Policy.**
  `
};

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    venueName: '',
    venueLocation: ''
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  
  // OTP States - Step 1: Email verification
  const [step, setStep] = useState(1); // Step 1: Email, Step 2: OTP, Step 3: Details
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  
  // Math CAPTCHA state
  const [captchaVerified, setCaptchaVerified] = useState(false);
  
  const navigate = useNavigate();
  const { toast } = useToast();

  // Start resend timer
  const startResendTimer = () => {
    setResendTimer(60);
    const interval = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Send OTP
  // Send OTP - Step 1
  const handleSendOtp = async () => {
    if (!validateEmail(formData.email)) {
      setOtpError('Please enter a valid email address');
      return;
    }
    
    setOtpLoading(true);
    setOtpError('');
    
    try {
      const response = await otpAPI.send(formData.email, 'registration', 'New User');
      if (response.success) {
        setOtpSent(true);
        setStep(2); // Move to OTP entry step
        startResendTimer();
        toast({
          title: 'OTP Sent',
          description: `Verification code sent to ${formData.email}`
        });
      }
    } catch (err) {
      setOtpError(err.message || 'Failed to send OTP');
      toast({
        title: 'Error',
        description: err.message || 'Failed to send OTP',
        variant: 'destructive'
      });
    } finally {
      setOtpLoading(false);
    }
  };

  // Verify OTP - Step 2
  const handleVerifyOtp = async () => {
    if (otp.length !== 4) {
      setOtpError('Please enter 4-digit OTP');
      return;
    }
    
    setOtpLoading(true);
    setOtpError('');
    
    try {
      const response = await otpAPI.verify(formData.email, otp, 'registration');
      if (response.success) {
        setOtpVerified(true);
        setStep(3); // Move to details step
        toast({
          title: 'Email Verified ✓',
          description: 'Please complete your registration details'
        });
      }
    } catch (err) {
      setOtpError(err.message || 'Invalid OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    await handleSendOtp();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Format phone number as user types
    if (name === 'phone') {
      processedValue = formatPhoneNumber(value);
    }
    
    // Reset OTP verification if email changes
    if (name === 'email' && otpVerified) {
      setOtpVerified(false);
      setOtpSent(false);
      setOtp('');
    }
    
    setFormData(prev => ({ ...prev, [name]: processedValue }));
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateFormData = () => {
    const errors = {};
    
    // Email validation
    if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email (e.g., abc@yourname.com)';
    }
    
    // Phone validation
    if (!validatePhone(formData.phone)) {
      errors.phone = 'Please enter a valid 10-digit phone number';
    }
    
    // Name validation
    if (!formData.name || formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }
    
    // Venue name validation
    if (!formData.venueName || formData.venueName.trim().length < 2) {
      errors.venueName = 'Venue name must be at least 2 characters';
    }
    
    // Password validation - must match backend requirements
    if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (!/[A-Z]/.test(formData.password)) {
      errors.password = 'Must contain an uppercase letter';
    } else if (!/[a-z]/.test(formData.password)) {
      errors.password = 'Must contain a lowercase letter';
    } else if (!/\d/.test(formData.password)) {
      errors.password = 'Must contain a number';
    } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
      errors.password = 'Must contain a special character (!@#$%^&*...)';
    }
    
    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    // Terms and Conditions validation
    if (!acceptedTerms) {
      errors.terms = 'You must accept the Terms and Conditions to register';
    }
    
    // CAPTCHA validation
    if (!captchaVerified) {
      errors.captcha = 'Please complete the security check';
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setLoading(true);

    // Check OTP verification first
    if (!otpVerified) {
      setError('Please verify your email with OTP before registering');
      setLoading(false);
      toast({
        title: 'Email Not Verified',
        description: 'Please verify your email with OTP first',
        variant: 'destructive'
      });
      return;
    }

    // Validate all fields
    const errors = validateFormData();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setLoading(false);
      
      // Show toast for terms error
      if (errors.terms) {
        toast({
          title: 'Terms Required',
          description: 'Please accept the Terms and Conditions to continue',
          variant: 'destructive'
        });
      }
      
      // Show toast for CAPTCHA error
      if (errors.captcha) {
        toast({
          title: 'Security Check Required',
          description: 'Please solve the math problem to verify you are human',
          variant: 'destructive'
        });
      }
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      // Extract digits only from phone for storage
      const phoneDigits = getPhoneDigits(formData.phone);

      // Register new admin user
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          phone: phoneDigits,
          venueName: formData.venueName,
          venueLocation: formData.venueLocation || null,
          role: 'admin',
          acceptedTerms: true,
          termsAcceptedAt: new Date().toISOString()
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.detail || 'Registration failed');
        setLoading(false);
        return;
      }

      const data = await response.json();

      // Auto-login immediately after successful registration
      try {
        const loginResponse = await fetch(`${API_URL}/api/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password
          }),
        });

        if (loginResponse.ok) {
          const loginData = await loginResponse.json();
          
          // Store auth data
          localStorage.setItem('token', loginData.access_token);
          localStorage.setItem('currentUser', JSON.stringify(loginData.user));
          
          toast({ 
            title: 'Success!', 
            description: '🎉 Account created! Your 15-day free trial is now active!' 
          });
          
          // Redirect to dashboard
          setTimeout(() => navigate('/dashboard'), 800);
        } else {
          toast({ 
            title: 'Success!', 
            description: 'Account created! Please login to continue.' 
          });
          setTimeout(() => navigate('/login'), 1000);
        }
      } catch (error) {
        console.error('Auto-login error:', error);
        toast({ 
          title: 'Success!', 
          description: 'Account created! Please login to continue.' 
        });
        setTimeout(() => navigate('/login'), 1000);
      }

      setLoading(false);
    } catch (error) {
      console.error('Registration error:', error);
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-cyan-50 to-orange-50 p-2 sm:p-4 relative">
      <div className="w-full max-w-2xl">
        {/* Header - Compact on mobile */}
        <div className="text-center mb-4 sm:mb-8">
          <div className="flex justify-center mb-2 sm:mb-4">
            <MVMLogo size="lg" />
          </div>
          <h1 className="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
            MyVenueMate
          </h1>
          <p className="text-xs sm:text-base text-gray-600 dark:text-muted-foreground mt-1 sm:mt-2">Venue Management Made Easy</p>
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader className="p-3 sm:p-6 pb-2 sm:pb-4">
            <CardTitle className="text-base sm:text-xl dark:text-foreground">
              {step === 1 && 'Step 1: Verify Email'}
              {step === 2 && 'Step 2: Enter OTP'}
              {step === 3 && 'Step 3: Complete Registration'}
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              {step === 1 && 'Enter your email to receive a verification code'}
              {step === 2 && `Enter the 4-digit code sent to ${formData.email}`}
              {step === 3 && 'Fill in your details to complete registration'}
            </CardDescription>
            {/* Step indicator */}
            <div className="flex items-center gap-2 mt-3">
              <div className={`h-2 flex-1 rounded-full ${step >= 1 ? 'bg-purple-600' : 'bg-gray-200'}`}></div>
              <div className={`h-2 flex-1 rounded-full ${step >= 2 ? 'bg-purple-600' : 'bg-gray-200'}`}></div>
              <div className={`h-2 flex-1 rounded-full ${step >= 3 ? 'bg-purple-600' : 'bg-gray-200'}`}></div>
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            
            {/* Step 1: Email Entry */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm">Email Address *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-muted-foreground" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="abc@yourname.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="pl-10 h-11 text-base"
                      required
                    />
                  </div>
                  {otpError && <p className="text-xs text-red-600">{otpError}</p>}
                </div>
                <Button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={otpLoading || !formData.email || !validateEmail(formData.email)}
                  className="w-full h-11 bg-purple-600 hover:bg-purple-700"
                >
                  {otpLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                  Send Verification Code
                </Button>
                <p className="text-center text-xs text-gray-500 dark:text-muted-foreground">
                  Already have an account? <a href="/login" className="text-purple-600 hover:underline">Login here</a>
                </p>
              </div>
            )}

            {/* Step 2: OTP Entry */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="text-center py-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full mb-4">
                    <Mail className="w-8 h-8 text-purple-600" />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-muted-foreground mb-2">We sent a code to</p>
                  <p className="font-semibold text-gray-900 dark:text-foreground">{formData.email}</p>
                </div>
                <div className="flex justify-center">
                  <Input
                    type="text"
                    maxLength={4}
                    value={otp}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setOtp(val);
                      setOtpError('');
                    }}
                    placeholder="0000"
                    className="text-center text-3xl font-bold tracking-[0.5em] w-44 h-14"
                  />
                </div>
                {otpError && <p className="text-center text-sm text-red-600">{otpError}</p>}
                <p className="text-center text-xs text-gray-500 dark:text-muted-foreground">OTP valid for 30 minutes</p>
                <Button
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={otpLoading || otp.length !== 4}
                  className="w-full h-11 bg-purple-600 hover:bg-purple-700"
                >
                  {otpLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                  Verify OTP
                </Button>
                <div className="flex items-center justify-between">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => { setStep(1); setOtp(''); setOtpError(''); }}
                    className="text-sm"
                  >
                    ← Change Email
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleResendOtp}
                    disabled={resendTimer > 0 || otpLoading}
                    className="text-sm"
                  >
                    {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Registration Form */}
            {step === 3 && (
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              {error && (
                <Alert variant="destructive" className="py-2 text-xs sm:text-sm">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Verified Email Display */}
              <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-lg">
                <ShieldCheck className="w-5 h-5 text-green-600" />
                <div className="flex-1">
                  <p className="text-xs text-green-600 font-medium">Email Verified</p>
                  <p className="text-sm text-green-800">{formData.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1 sm:space-y-2">
                  <Label htmlFor="name" className="text-xs sm:text-sm">Full Name *</Label>
                  <div className="relative">
                    <User className="absolute left-2.5 sm:left-3 top-2.5 sm:top-3 h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 dark:text-muted-foreground" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`pl-8 sm:pl-10 h-9 sm:h-10 text-sm ${fieldErrors.name ? 'border-red-500' : ''}`}
                      required
                    />
                  </div>
                  {fieldErrors.name && (
                    <p className="text-xs text-red-600">{fieldErrors.name}</p>
                  )}
                </div>

                <div className="space-y-1 sm:space-y-2">
                  <Label htmlFor="venueName" className="text-xs sm:text-sm">Venue Name *</Label>
                  <div className="relative">
                    <Building2 className="absolute left-2.5 sm:left-3 top-2.5 sm:top-3 h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 dark:text-muted-foreground" />
                    <Input
                      id="venueName"
                      name="venueName"
                      type="text"
                      placeholder="Your venue name"
                      value={formData.venueName}
                      onChange={handleInputChange}
                      className={`pl-8 sm:pl-10 h-9 sm:h-10 text-sm ${fieldErrors.venueName ? 'border-red-500' : ''}`}
                      required
                    />
                  </div>
                  {fieldErrors.venueName && (
                    <p className="text-xs text-red-600">{fieldErrors.venueName}</p>
                  )}
                </div>

                <div className="space-y-1 sm:space-y-2">
                  <Label htmlFor="venueLocation" className="text-xs sm:text-sm">Venue Location / Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-2.5 sm:left-3 top-2.5 sm:top-3 h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 dark:text-muted-foreground" />
                    <Input
                      id="venueLocation"
                      name="venueLocation"
                      type="text"
                      placeholder="City, State or Full Address"
                      value={formData.venueLocation}
                      onChange={handleInputChange}
                      className="pl-8 sm:pl-10 h-9 sm:h-10 text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-1 sm:space-y-2">
                <Label htmlFor="phone" className="text-xs sm:text-sm">Phone Number * (10 digits)</Label>
                <div className="relative">
                  <Phone className="absolute left-2.5 sm:left-3 top-2.5 sm:top-3 h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 dark:text-muted-foreground" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="XXX-XXX-XXXX"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`pl-8 sm:pl-10 h-9 sm:h-10 text-sm ${fieldErrors.phone ? 'border-red-500' : ''}`}
                    maxLength="12"
                    required
                  />
                </div>
                {fieldErrors.phone && (
                  <p className="text-xs text-red-600">{fieldErrors.phone}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1 sm:space-y-2">
                  <Label htmlFor="password" className="text-xs sm:text-sm">Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-2.5 sm:left-3 top-2.5 sm:top-3 h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 dark:text-muted-foreground" />
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="Strong password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={`pl-8 sm:pl-10 h-9 sm:h-10 text-sm ${fieldErrors.password ? 'border-red-500' : ''}`}
                      required
                    />
                  </div>
                  {fieldErrors.password ? (
                    <p className="text-xs text-red-600">{fieldErrors.password}</p>
                  ) : (
                    <p className="text-[10px] sm:text-xs text-gray-500 dark:text-muted-foreground">8+ chars, upper, lower, number, special</p>
                  )}
                </div>

                <div className="space-y-1 sm:space-y-2">
                  <Label htmlFor="confirmPassword" className="text-xs sm:text-sm">Confirm Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-2.5 sm:left-3 top-2.5 sm:top-3 h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 dark:text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder="Confirm password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className={`pl-8 sm:pl-10 h-9 sm:h-10 text-sm ${fieldErrors.confirmPassword ? 'border-red-500' : ''}`}
                      required
                    />
                  </div>
                  {fieldErrors.confirmPassword && (
                    <p className="text-xs text-red-600">{fieldErrors.confirmPassword}</p>
                  )}
                </div>
              </div>

              {/* Math CAPTCHA Security Check */}
              <div className={`${fieldErrors.captcha ? 'ring-2 ring-red-300 rounded-lg' : ''}`}>
                <MathCaptcha 
                  onVerify={setCaptchaVerified} 
                  verified={captchaVerified}
                  onRefresh={() => setCaptchaVerified(false)}
                />
                {fieldErrors.captcha && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <Calculator className="w-3 h-3" />
                    {fieldErrors.captcha}
                  </p>
                )}
              </div>

              {/* Terms and Conditions Checkbox - Mobile Optimized */}
              <div className={`p-2.5 sm:p-4 rounded-lg border-2 ${fieldErrors.terms ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-border bg-gray-50 dark:bg-secondary'}`}>
                <div className="flex items-start space-x-2 sm:space-x-3">
                  <Checkbox 
                    id="terms" 
                    checked={acceptedTerms}
                    onCheckedChange={(checked) => {
                      setAcceptedTerms(checked);
                      if (fieldErrors.terms) {
                        setFieldErrors(prev => ({ ...prev, terms: '' }));
                      }
                    }}
                    className="mt-0.5 h-4 w-4 sm:h-5 sm:w-5"
                  />
                  <div className="flex-1">
                    <Label 
                      htmlFor="terms" 
                      className="text-xs sm:text-sm font-medium leading-relaxed cursor-pointer"
                    >
                      I have read and agree to the{' '}
                      <button
                        type="button"
                        onClick={() => setShowTermsDialog(true)}
                        className="text-teal-600 hover:text-teal-700 underline font-semibold"
                      >
                        Terms and Conditions
                      </button>
                      {' '}and{' '}
                      <button
                        type="button"
                        onClick={() => setShowPrivacyDialog(true)}
                        className="text-teal-600 hover:text-teal-700 underline font-semibold"
                      >
                        Privacy Policy
                      </button>
                      {' *'}
                    </Label>
                    <p className="text-[10px] sm:text-xs text-gray-500 dark:text-muted-foreground mt-0.5 sm:mt-1">
                      By checking this box, you confirm that you accept our terms
                    </p>
                  </div>
                </div>
                {fieldErrors.terms && (
                  <p className="text-xs text-red-600 mt-1.5 sm:mt-2 flex items-center gap-1">
                    <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                    {fieldErrors.terms}
                  </p>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full h-9 sm:h-10 text-sm sm:text-base bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 transition-all duration-300 shadow-md"
                disabled={loading}
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>
            )} {/* End of Step 3 */}

            {step === 3 && (
            <div className="mt-4 sm:mt-6 text-center">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="font-semibold text-teal-600 hover:text-teal-700 transition-colors"
                >
                  Sign in
                </button>
              </p>
            </div>
            )}
          </CardContent>
        </Card>
        
        {/* Powered by footer */}
        <div className="mt-4 sm:mt-8 text-center">
          <p className="text-[10px] sm:text-xs text-gray-500 dark:text-muted-foreground">
            Powered by <span className="font-semibold text-teal-600">MYONK</span>
          </p>
        </div>
      </div>

      {/* Terms and Conditions Dialog - Mobile Optimized */}
      <Dialog open={showTermsDialog} onOpenChange={setShowTermsDialog}>
        <DialogContent className="w-[95vw] max-w-2xl h-[90vh] sm:h-auto sm:max-h-[80vh] overflow-hidden flex flex-col p-3 sm:p-6 rounded-lg">
          <DialogHeader className="pb-2 sm:pb-4">
            <DialogTitle className="flex items-center gap-2 text-base sm:text-xl">
              <ScrollText className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600" />
              Terms and Conditions
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Last updated: {TERMS_AND_CONDITIONS.lastUpdated}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto pr-1 sm:pr-2 -mr-1 sm:-mr-2">
            <div className="prose prose-sm max-w-none text-gray-700 dark:text-foreground">
              {TERMS_AND_CONDITIONS.content.split('\n').map((line, index) => {
                // Handle headers - Mobile optimized sizes
                if (line.startsWith('## ')) {
                  return <h2 key={index} className="text-base sm:text-xl font-bold text-gray-900 dark:text-foreground mt-3 sm:mt-4 mb-1.5 sm:mb-2">{line.replace('## ', '')}</h2>;
                }
                if (line.startsWith('### ')) {
                  return <h3 key={index} className="text-sm sm:text-lg font-semibold text-gray-800 dark:text-foreground mt-3 sm:mt-4 mb-1.5 sm:mb-2">{line.replace('### ', '')}</h3>;
                }
                if (line.startsWith('**') && line.endsWith('**')) {
                  return <p key={index} className="font-bold text-gray-900 dark:text-foreground mt-1.5 sm:mt-2 text-xs sm:text-base">{line.replace(/\*\*/g, '')}</p>;
                }
                if (line.startsWith('- ')) {
                  return <li key={index} className="ml-3 sm:ml-4 text-gray-700 dark:text-foreground text-xs sm:text-base">{line.replace('- ', '')}</li>;
                }
                if (line === '---') {
                  return <hr key={index} className="my-3 sm:my-4 border-gray-200 dark:border-border" />;
                }
                if (line.trim() === '') {
                  return <br key={index} />;
                }
                return <p key={index} className="text-gray-700 dark:text-foreground leading-relaxed text-xs sm:text-base">{line}</p>;
              })}
            </div>
          </div>
          
          <DialogFooter className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowTermsDialog(false)}
              className="w-full sm:w-auto h-9 sm:h-10 text-sm"
            >
              Close
            </Button>
            <Button
              onClick={() => {
                setAcceptedTerms(true);
                setShowTermsDialog(false);
                if (fieldErrors.terms) {
                  setFieldErrors(prev => ({ ...prev, terms: '' }));
                }
              }}
              className="w-full sm:w-auto h-9 sm:h-10 text-sm bg-teal-600 hover:bg-teal-700"
            >
              I Accept
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Privacy Policy Dialog - Mobile Optimized */}
      <Dialog open={showPrivacyDialog} onOpenChange={setShowPrivacyDialog}>
        <DialogContent className="w-[95vw] max-w-2xl h-[90vh] sm:h-auto sm:max-h-[80vh] overflow-hidden flex flex-col p-3 sm:p-6 rounded-lg">
          <DialogHeader className="pb-2 sm:pb-4">
            <DialogTitle className="flex items-center gap-2 text-base sm:text-xl">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600" />
              Privacy Policy
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Last updated: {PRIVACY_POLICY.lastUpdated}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto pr-1 sm:pr-2 -mr-1 sm:-mr-2">
            <div className="prose prose-sm max-w-none text-gray-700 dark:text-foreground">
              {PRIVACY_POLICY.content.split('\n').map((line, index) => {
                // Handle headers - Mobile optimized sizes
                if (line.startsWith('## ')) {
                  return <h2 key={index} className="text-base sm:text-xl font-bold text-gray-900 dark:text-foreground mt-3 sm:mt-4 mb-1.5 sm:mb-2">{line.replace('## ', '')}</h2>;
                }
                if (line.startsWith('### ')) {
                  return <h3 key={index} className="text-sm sm:text-lg font-semibold text-gray-800 dark:text-foreground mt-3 sm:mt-4 mb-1.5 sm:mb-2">{line.replace('### ', '')}</h3>;
                }
                if (line.startsWith('**') && line.endsWith('**')) {
                  return <p key={index} className="font-bold text-gray-900 dark:text-foreground mt-1.5 sm:mt-2 text-xs sm:text-base">{line.replace(/\*\*/g, '')}</p>;
                }
                if (line.startsWith('- ')) {
                  return <li key={index} className="ml-3 sm:ml-4 text-gray-700 dark:text-foreground text-xs sm:text-base">{line.replace('- ', '')}</li>;
                }
                if (line === '---') {
                  return <hr key={index} className="my-3 sm:my-4 border-gray-200 dark:border-border" />;
                }
                if (line.trim() === '') {
                  return <br key={index} />;
                }
                return <p key={index} className="text-gray-700 dark:text-foreground leading-relaxed text-xs sm:text-base">{line}</p>;
              })}
            </div>
          </div>
          
          <DialogFooter className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowPrivacyDialog(false)}
              className="w-full sm:w-auto h-9 sm:h-10 text-sm"
            >
              Close
            </Button>
            <Button
              onClick={() => {
                setAcceptedTerms(true);
                setShowPrivacyDialog(false);
                if (fieldErrors.terms) {
                  setFieldErrors(prev => ({ ...prev, terms: '' }));
                }
              }}
              className="w-full sm:w-auto h-9 sm:h-10 text-sm bg-teal-600 hover:bg-teal-700"
            >
              I Accept
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Register;
